import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { collection, getDocs, doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase/firebase';
import WIcon from '../../components/shared/WIcon';
import Modal from '../../components/shared/Modal';

const PRIMARY_ADMIN_EMAIL = 'ntch29@gmail.com';

const initialsFor = (email) => (email || '?').split('@')[0].slice(0, 2).toUpperCase();

const formatDate = (value) => {
  if (!value) return '—';
  const d = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const AddAdminModal = ({ existingEmails, onClose, onSaved }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.toLowerCase().trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (existingEmails.has(trimmed)) {
      setError('This email is already a super admin.');
      return;
    }
    setSubmitting(true);
    try {
      // Double-check we're not overwriting an existing doc.
      const existing = await getDoc(doc(db, 'superAdmins', trimmed));
      if (existing.exists()) {
        throw new Error('This email is already a super admin.');
      }
      await setDoc(doc(db, 'superAdmins', trimmed), {
        email: trimmed,
        name: name.trim() || null,
        addedAt: new Date(),
        addedBy: auth.currentUser?.email || null,
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to add super admin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Add super admin" onClose={onClose} width={460}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <div className="loysta-alert error">{error}</div>}

        <div className="form-group">
          <label>Google account email *</label>
          <input
            type="email"
            className="form-control"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
            required
          />
          <span className="form-hint">Must be a Google account. The user signs in with this email via Google SSO.</span>
        </div>

        <div className="form-group">
          <label>Name <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
          <input
            type="text"
            className="form-control"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Doe"
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button type="button" className="cancel-btn" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={submitting} style={{ flex: 1, justifyContent: 'center' }}>
            {submitting ? 'Adding…' : 'Add super admin'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

const SuperAdminSettings = () => {
  const { registerPageContext } = useOutletContext();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [error, setError] = useState('');
  const currentEmail = (auth.currentUser?.email || '').toLowerCase();

  const fetchAdmins = useCallback(async () => {
    setError('');
    try {
      const snap = await getDocs(collection(db, 'superAdmins'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const aEmail = (a.email || a.id).toLowerCase();
        const bEmail = (b.email || b.id).toLowerCase();
        // Primary admin always first
        if (aEmail === PRIMARY_ADMIN_EMAIL) return -1;
        if (bEmail === PRIMARY_ADMIN_EMAIL) return 1;
        return aEmail.localeCompare(bEmail);
      });
      setAdmins(list);
    } catch (err) {
      console.error('Could not list super admins:', err);
      setError('Could not load the list of super admins. You may not have permission to read this collection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAdmins(); }, [fetchAdmins]);

  useEffect(() => {
    registerPageContext({
      addLabel: 'Add super admin',
      onAdd: () => setShowAdd(true),
    });
  }, [registerPageContext]);

  const existingEmails = useMemo(
    () => new Set(admins.map(a => (a.email || a.id).toLowerCase())),
    [admins],
  );

  const performRemove = async () => {
    if (!confirmRemove) return;
    const target = (confirmRemove.email || confirmRemove.id).toLowerCase();
    if (target === PRIMARY_ADMIN_EMAIL) {
      window.alert('The primary admin cannot be removed.');
      setConfirmRemove(null);
      return;
    }
    if (target === currentEmail) {
      window.alert('You cannot remove yourself. Ask another super admin to do it.');
      setConfirmRemove(null);
      return;
    }
    try {
      await deleteDoc(doc(db, 'superAdmins', target));
      setConfirmRemove(null);
      fetchAdmins();
    } catch (err) {
      console.error(err);
      window.alert('Failed to remove. ' + (err.message || ''));
    }
  };

  return (
    <>
      {showAdd && (
        <AddAdminModal
          existingEmails={existingEmails}
          onClose={() => setShowAdd(false)}
          onSaved={fetchAdmins}
        />
      )}

      {confirmRemove && (
        <Modal title="Remove super admin" onClose={() => setConfirmRemove(null)} width={420}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.6, margin: 0 }}>
              Remove super admin access for <strong>{confirmRemove.email || confirmRemove.id}</strong>? They'll lose access to the admin portal immediately.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="cancel-btn" style={{ flex: 1 }} onClick={() => setConfirmRemove(null)}>Cancel</button>
              <button type="button" className="btn btn-danger" style={{ flex: 1, justifyContent: 'center' }} onClick={performRemove}>Remove</button>
            </div>
          </div>
        </Modal>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Intro card */}
        <div className="loysta-card" style={{ padding: '20px 22px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'rgba(0,191,99,0.10)', color: '#00bf63',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <WIcon name="settings" size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
              Super admin access
            </div>
            <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, margin: 0 }}>
              People with full access to the Loysta platform. Sign-in is via Google SSO — emails listed here are the whitelist.
              Add an email and that Google account can immediately sign in at <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, fontSize: 12 }}>/admin/login</code>.
            </p>
          </div>
          <span style={{
            padding: '4px 12px',
            borderRadius: 999,
            background: 'rgba(0,191,99,0.10)',
            color: '#00bf63',
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}>
            {loading ? '—' : admins.length} {admins.length === 1 ? 'admin' : 'admins'}
          </span>
        </div>

        <div className="loysta-card">
          <div className="loysta-card-head">
            <span className="loysta-card-title">Super admins</span>
            <button
              type="button"
              onClick={() => setShowAdd(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 14px', background: '#00bf63', color: 'white',
                border: 'none', borderRadius: 8,
                fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#009950'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#00bf63'; }}
            >
              <WIcon name="plus" size={13} color="white" />
              Add super admin
            </button>
          </div>

          {error && (
            <div style={{
              padding: '14px 20px',
              background: 'rgba(245,158,11,0.06)',
              borderBottom: '1px solid #f1f5f9',
              color: '#92400e',
              fontSize: 13,
              lineHeight: 1.55,
            }}>{error}</div>
          )}

          <table className="loysta-table">
            <thead>
              <tr>
                <th>Admin</th>
                <th>Added</th>
                <th>Added by</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading…</td></tr>
              ) : admins.length === 0 ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
                  No super admins visible. Click "Add super admin" to whitelist a Google account.
                </td></tr>
              ) : admins.map((a) => {
                const email = (a.email || a.id).toLowerCase();
                const isMe = email === currentEmail;
                const isPrimary = email === PRIMARY_ADMIN_EMAIL;
                return (
                  <tr key={email}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%',
                          background: isPrimary
                            ? 'linear-gradient(135deg, #00bf63, #c1ff72)'
                            : 'rgba(59,130,246,0.10)',
                          color: isPrimary ? '#0f172a' : '#3b82f6',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 800, flexShrink: 0,
                        }}>{initialsFor(email)}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            {a.name || email}
                            {isPrimary && (
                              <span style={{
                                padding: '2px 8px', borderRadius: 999,
                                background: 'rgba(0,191,99,0.12)', color: '#00bf63',
                                fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                              }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="11" width="18" height="11" rx="2" />
                                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                                PRIMARY
                              </span>
                            )}
                            {isMe && !isPrimary && (
                              <span style={{
                                padding: '2px 8px', borderRadius: 999,
                                background: 'rgba(59,130,246,0.10)', color: '#3b82f6',
                                fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                              }}>YOU</span>
                            )}
                            {isMe && isPrimary && (
                              <span style={{
                                padding: '2px 8px', borderRadius: 999,
                                background: 'rgba(59,130,246,0.10)', color: '#3b82f6',
                                fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                              }}>YOU</span>
                            )}
                          </div>
                          {a.name && (
                            <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {email}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ color: '#94a3b8', fontSize: 13 }}>{formatDate(a.addedAt)}</td>
                    <td style={{ color: '#64748b', fontSize: 13, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.addedBy || <span style={{ color: '#94a3b8' }}>—</span>}
                    </td>
                    <td>
                      <button
                        className="loysta-icon-btn danger"
                        onClick={() => setConfirmRemove(a)}
                        disabled={isPrimary || isMe}
                        title={
                          isPrimary
                            ? 'Primary admin cannot be removed'
                            : isMe
                              ? "You can't remove yourself"
                              : 'Remove access'
                        }
                        style={(isPrimary || isMe) ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
                      >
                        <WIcon name="trash" size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default SuperAdminSettings;
